import LinkIcon from "@/assets/icons/link.svg"
import NFTIcon from "@/assets/icons/nft.svg"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useAtomValue } from "jotai"
import { connectionAtom, networkAtom } from "@/atoms/settings"
import { getLinkExplorerAddress } from "@/components/dashboard/commons"
import Button from "@/components/base/Button"
import { RelendDialog } from "@/components/relendDialog"
import { NFTDetailType } from "@/hooks/useFetchNFT"
import useTrans from "@/hooks/useTrans"
import Box from "@mui/material/Box"
import Link from "@mui/material/Link"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import dayjs from "dayjs"
import Image from "next/image"
import { fetchTokensInfo } from "@renec-foundation/relend-adapter"
import { DEBUG_MODE } from "@/utils/constants"

type TokenData = {
  name: string
  symbol: string
  logoUri: string
  decimals: number
}

interface INFTDetailDialogProps {
  isOpen: boolean
  handleCloseModal: () => void
  selectedNFT: NFTDetailType
}

const NFTDetailDialog = ({ isOpen, handleCloseModal, selectedNFT }: INFTDetailDialogProps) => {
  const trans = useTrans()
  const network = useAtomValue(networkAtom)
  const connection = useAtomValue(connectionAtom)
  const [displayEligibleToken, setDisplayEligibleToken] = useState<string>()

  const fetchNFTEligibleTokens = useCallback(async () => {
    if (!selectedNFT.detail) return

    const eligibleTokens = selectedNFT.detail?.attributes.find((attr) => attr.trait_type === "eligible_tokens")
    if (!eligibleTokens || !eligibleTokens.value) return
    const tokens = eligibleTokens.value.split(",").map((token) => token.trim())

    const dataTokens: TokenData[] = await fetchTokensInfo(network, tokens, connection, DEBUG_MODE)
    const result = Object.values(dataTokens)
      .map((item: TokenData) => item.name)
      .join(", ")

    setDisplayEligibleToken(result)
  }, [connection, network, selectedNFT])

  useEffect(() => {
    fetchNFTEligibleTokens()
  }, [fetchNFTEligibleTokens])

  const detailNFT = useMemo(() => {
    let displayStartDate = ""
    const startDate = selectedNFT.detail?.attributes.find((attr) => attr.trait_type === "start_time")
    if (startDate) {
      const [day, month, year] = startDate.value.split(" ")[0].split("/")
      const formattedDate = `${year}-${month}-${day}`
      displayStartDate = dayjs(formattedDate).format("DD/MM/YYYY")
    }

    let displayEndDate = ""
    const endDate = selectedNFT.detail?.attributes.find((attr) => attr.trait_type === "end_time")
    if (endDate) {
      const [day, month, year] = endDate.value.split(" ")[0].split("/")
      const formattedDate = `${year}-${month}-${day}`
      displayEndDate = dayjs(formattedDate).format("DD/MM/YYYY")
    }

    let displayDiscountPercentage = ""
    const discountPercentage = selectedNFT.detail?.attributes.find((attr) => attr.trait_type === "discount_percentage")
    if (discountPercentage) displayDiscountPercentage = `${discountPercentage.value}% OFF`

    let displayEligible = ""
    const eligibleTokens = selectedNFT.detail?.attributes.find((attr) => attr.trait_type === "eligible_tokens")
    if (eligibleTokens) {
      if (!eligibleTokens.value) displayEligible = trans.dashboard.modal.nft_more_all_token
      else {
        displayEligible = ""
      }
    }

    return {
      displayStartDate,
      displayEndDate,
      displayDiscountPercentage,
      displayEligible,
    }
  }, [trans, selectedNFT])

  return (
    <RelendDialog
      open={isOpen}
      handleClose={handleCloseModal}
      title={trans.dashboard.modal.nft_detail}
      data-testid='modal-detail-nft'
      buttons={
        <Button data-testid='button-close-detail-nft' fullWidth variant='outlined' onClick={handleCloseModal}>
          {trans.dashboard.modal.close}
        </Button>
      }
    >
      <Stack gap={2}>
        <Stack alignItems='center' gap={1}>
          <Image
            src={selectedNFT.imageUrl ?? NFTIcon}
            alt='staking-icon'
            width={97}
            height={97}
            className='sm:w-32 sm:h-32'
          />
          <Typography className='text-neutral-900 text-sm sm:text-base font-semibold'>{selectedNFT.name}</Typography>
          <Link
            href={getLinkExplorerAddress(network, selectedNFT.mintAddress)}
            target='_blank'
            className='flex flex-row gap-1 text-neutral-600 text-xs sm:text-sm'
          >
            <Image src={LinkIcon} width={12} height={12} alt='nft-image' />
            <span>{selectedNFT.displayMintAddress}</span>
          </Link>
        </Stack>

        <Stack gap={1}>
          <Typography className='text-neutral-600 text-[13px] sm:text-sm'>
            {trans.dashboard.modal.nft_description}
          </Typography>
          <Box className='rounded-lg border border-solid border-secondary bg-[#F7F8F8] px-4 py-3'>
            <Typography className='text-neutral-900 text-[13px] sm:text-sm'>
              {selectedNFT.detail?.description}
            </Typography>
          </Box>
        </Stack>

        <Stack gap={1}>
          <Typography className='text-neutral-600 text-[13px] sm:text-sm'>
            {trans.dashboard.modal.nft_more_info}
          </Typography>
          <Box className='rounded-lg border border-solid border-secondary bg-[#F7F8F8] px-4 py-3 flex gap-1 flex-col'>
            <Typography className='text-neutral-900 text-[13px] sm:text-sm'>
              {trans.dashboard.modal.nft_more_info_start_date_end_date}: {detailNFT.displayStartDate} -{" "}
              {detailNFT.displayEndDate}
            </Typography>
            <Typography className='text-neutral-900 text-[13px] sm:text-sm'>
              {trans.dashboard.modal.nft_more_info_discount_percentage}: {detailNFT.displayDiscountPercentage}
            </Typography>
            <Typography className='text-neutral-900 text-[13px] sm:text-sm'>
              {trans.dashboard.modal.nft_more_info_eligible_token}: {displayEligibleToken ?? detailNFT.displayEligible}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </RelendDialog>
  )
}

export default NFTDetailDialog
